import type { ButtonHTMLAttributes, ReactNode } from 'react';

type ButtonVariant = 'primary' | 'secondary' | 'outlined' | 'error' | 'ghost';
type ButtonSize = 'sm' | 'md' | 'lg';
type IconPosition = 'left' | 'right';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  icon?: ReactNode;
  iconPosition?: IconPosition;
  loading?: boolean;
}

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 gap-1.5 text-sm rounded-[12px]',
  md: 'px-4 py-2 gap-2 text-sm rounded-[16px]',
  lg: 'px-6 py-3 gap-2 text-base rounded-[16px]',
};

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'bg-primary text-white border border-primary shadow-sm hover:opacity-90 active:opacity-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed',
  secondary:
    'bg-secondary text-gray-900 border border-secondary shadow-sm hover:opacity-90 active:opacity-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed',
  outlined:
    'bg-transparent text-primary border-2 border-primary hover:bg-primary/5 active:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed',
  error:
    'bg-red-600 text-white border border-red-600 shadow-sm hover:bg-red-700 active:bg-red-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed',
  ghost:
    'bg-transparent text-primary border border-transparent hover:bg-surface-gray-light active:bg-surface-gray-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed',
};

export function Button({
  variant = 'primary',
  size = 'md',
  fullWidth,
  icon,
  iconPosition = 'left',
  loading,
  children,
  disabled,
  className = '',
  ...rest
}: ButtonProps) {
  const isDisabled = disabled || loading;
  const sizeCls = sizeClasses[size];
  const variantCls = variantClasses[variant];

  const base =
    'inline-flex items-center justify-center border-solid cursor-pointer font-sans transition-all duration-150';

  const fullWidthCls = fullWidth ? 'w-full' : '';
  const iconOnlyCls = icon && !children ? (size === 'sm' ? 'p-1.5' : size === 'md' ? 'p-2' : 'p-3') : '';

  const classes = [base, sizeCls, variantCls, fullWidthCls, iconOnlyCls, className].filter(Boolean).join(' ');

  return (
    <button
      type="button"
      className={classes}
      disabled={isDisabled}
      data-variant={variant}
      data-size={size}
      {...rest}
    >
      {loading && (
        <span
          className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin me-2"
          aria-hidden
        />
      )}
      {icon && iconPosition === 'left' && <span className="inline-flex items-center justify-center shrink-0">{icon}</span>}
      {children && <span className="whitespace-nowrap">{children}</span>}
      {icon && iconPosition === 'right' && <span className="inline-flex items-center justify-center shrink-0">{icon}</span>}
    </button>
  );
}
